/* Eloence CRO — conversion-rate-optimization widgets.
   Runs on eloence templates only (gated in layout/theme.liquid).
   All public APIs live on window.ELOENCE_CRO.
   Events emitted on document: eloence:variant:changed, eloence:wishlist:changed.
   Event listened: eloence:variant:changed (echoed from eloence-ui.js via eloence:variant-change).
   Vanilla JS. No external dependencies. */
(function () {
  'use strict';

  var API = {};

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function debug(e) {
    if (window.console && console.debug) console.debug('[eloence-cro]', e);
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  /* =====================================================================
     1. Live countdown
     Reads [data-el-countdown] with:
       data-deadline-hours="20:00"     (HH:MM local cutoff — default "20:00")
       data-delivery-days="1"          (business days after cutoff — default 1)
       data-lang="fr"                  (default "fr")
     Renders into [data-el-countdown-text] child every 1s.
     Format FR: "Commandez avant HH:MM:SS — livraison <weekday> <day> <month>"
     Past-cutoff: "Expédition demain"
  ===================================================================== */
  var _countdownTimer = null;

  var FR_WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  var FR_MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                   'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  var EN_WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                   'July', 'August', 'September', 'October', 'November', 'December'];

  /* Skip Sun(0) & Sat(6) when stepping forward by business days */
  function addBusinessDays(date, n) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      var dow = d.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    return d;
  }

  function parseHours(str) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(str || '20:00'));
    if (!m) return { h: 20, mm: 0 };
    return { h: Math.min(23, parseInt(m[1], 10)), mm: Math.min(59, parseInt(m[2], 10)) };
  }

  function formatDeliveryDate(date, lang) {
    var weekdays = lang === 'en' ? EN_WEEKDAYS : FR_WEEKDAYS;
    var months = lang === 'en' ? EN_MONTHS : FR_MONTHS;
    return weekdays[date.getDay()] + ' ' + date.getDate() + ' ' + months[date.getMonth()];
  }

  function renderCountdown(el) {
    var textEl = el.querySelector('[data-el-countdown-text]');
    if (!textEl) return;

    var cutoff = parseHours(el.getAttribute('data-deadline-hours'));
    var deliveryDays = Math.max(1, parseInt(el.getAttribute('data-delivery-days') || '1', 10));
    var lang = (el.getAttribute('data-lang') || document.documentElement.getAttribute('lang') || 'fr').slice(0, 2);

    var now = new Date();
    var deadline = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoff.h, cutoff.mm, 0, 0);

    /* Past cutoff — show fallback and ship next business day */
    if (now >= deadline) {
      var msgPast = lang === 'en' ? 'Ships tomorrow' : 'Expédition demain';
      textEl.textContent = msgPast;
      el.classList.add('is-past-cutoff');
      return;
    }

    el.classList.remove('is-past-cutoff');

    var diffMs = deadline - now;
    var diffSec = Math.floor(diffMs / 1000);
    var h = Math.floor(diffSec / 3600);
    var m = Math.floor((diffSec % 3600) / 60);
    var s = diffSec % 60;

    var deliveryDate = addBusinessDays(now, deliveryDays);
    var deliveryStr = formatDeliveryDate(deliveryDate, lang);

    var timeStr = pad(h) + ':' + pad(m) + ':' + pad(s);
    var line = lang === 'en'
      ? 'Order within ' + timeStr + ' — delivery ' + deliveryStr
      : 'Commandez avant ' + timeStr + ' — livraison ' + deliveryStr;

    textEl.textContent = line;
  }

  function initCountdowns() {
    var els = document.querySelectorAll('[data-el-countdown]');
    if (!els.length) return;
    els.forEach(renderCountdown);
    /* Single shared tick — tick every 1s, updates all instances */
    if (_countdownTimer) clearInterval(_countdownTimer);
    _countdownTimer = setInterval(function () {
      document.querySelectorAll('[data-el-countdown]').forEach(renderCountdown);
    }, 1000);
    /* Pause while tab hidden to save battery */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && _countdownTimer) {
        clearInterval(_countdownTimer);
        _countdownTimer = null;
      } else if (!document.hidden && !_countdownTimer) {
        document.querySelectorAll('[data-el-countdown]').forEach(renderCountdown);
        _countdownTimer = setInterval(function () {
          document.querySelectorAll('[data-el-countdown]').forEach(renderCountdown);
        }, 1000);
      }
    });
  }

  API.countdown = { render: renderCountdown };

  /* =====================================================================
     2. Stock level bar
     [data-el-stock="N"] — sets .el-stock__fill width + color tier class.
     data-hide-above="50" (default) auto-hides when N > that OR N <= 0.
     Tiers: <5 → is-low (rouge), 5-20 → is-mid (beige-warm), 20+ → is-ok (success).
  ===================================================================== */
  function renderStockBar(el) {
    var n = parseInt(el.getAttribute('data-el-stock') || '0', 10);
    var hideAbove = parseInt(el.getAttribute('data-hide-above') || '50', 10);

    if (n <= 0 || n > hideAbove) {
      el.setAttribute('hidden', '');
      return;
    }
    el.removeAttribute('hidden');

    /* Percent width: 5% min for visibility, scaled to hideAbove ceiling */
    var pct = Math.max(5, Math.min(100, Math.round((n / hideAbove) * 100)));
    var fill = el.querySelector('[data-el-stock-fill]');
    if (fill) fill.style.width = pct + '%';

    el.classList.remove('is-low', 'is-mid', 'is-ok');
    if (n < 5) el.classList.add('is-low');
    else if (n <= 20) el.classList.add('is-mid');
    else el.classList.add('is-ok');
  }

  function initStockBars() {
    document.querySelectorAll('[data-el-stock]').forEach(renderStockBar);
  }

  API.stock = { render: renderStockBar };

  /* =====================================================================
     3. Image zoom on hover (desktop-only)
     [data-el-zoom] wrapper containing [data-el-zoom-target].
     On mousemove sets --zoom-x / --zoom-y CSS vars (0–100%) on wrapper.
     Disabled when (pointer: coarse) — touch devices skip entirely.
  ===================================================================== */
  function initZoom(wrap) {
    if (wrap.dataset.elZoomInit === '1') return;
    wrap.dataset.elZoomInit = '1';

    var target = wrap.querySelector('[data-el-zoom-target]');
    if (!target) return;

    function onMove(e) {
      var r = wrap.getBoundingClientRect();
      var x = ((e.clientX - r.left) / r.width) * 100;
      var y = ((e.clientY - r.top) / r.height) * 100;
      /* Clamp 0–100 in case of fast exits */
      x = Math.max(0, Math.min(100, x));
      y = Math.max(0, Math.min(100, y));
      wrap.style.setProperty('--zoom-x', x + '%');
      wrap.style.setProperty('--zoom-y', y + '%');
    }
    function onEnter() { wrap.classList.add('is-zooming'); }
    function onLeave() {
      wrap.classList.remove('is-zooming');
      wrap.style.setProperty('--zoom-x', '50%');
      wrap.style.setProperty('--zoom-y', '50%');
    }

    wrap.addEventListener('mousemove', onMove);
    wrap.addEventListener('mouseenter', onEnter);
    wrap.addEventListener('mouseleave', onLeave);
  }

  function initZooms() {
    /* Skip on coarse pointer (touch primary) */
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('[data-el-zoom]').forEach(initZoom);
  }

  /* =====================================================================
     4. Lightbox dialog
     [data-el-lightbox-trigger] clicks open <dialog data-el-lightbox>.
     Triggers sharing the same data-el-lightbox-group form a gallery.
     Escape closes. ArrowLeft/ArrowRight navigate. Focus trapped.
  ===================================================================== */
  var _lightboxState = { index: 0, items: [], dialog: null };

  function getLightboxDialog() {
    return document.querySelector('[data-el-lightbox]');
  }

  function updateLightbox() {
    var dlg = _lightboxState.dialog;
    if (!dlg) return;
    var img = dlg.querySelector('[data-el-lightbox-img]');
    var counter = dlg.querySelector('[data-el-lightbox-counter]');
    var item = _lightboxState.items[_lightboxState.index];
    if (!item) return;
    if (img) {
      img.src = item.src;
      img.alt = item.alt || '';
    }
    if (counter) {
      counter.textContent = (_lightboxState.index + 1) + ' / ' + _lightboxState.items.length;
    }
  }

  function openLightbox(triggerEl) {
    var dlg = getLightboxDialog();
    if (!dlg || typeof dlg.showModal !== 'function') return;
    _lightboxState.dialog = dlg;

    var group = triggerEl.getAttribute('data-el-lightbox-group') || '';
    var selector = group
      ? '[data-el-lightbox-trigger][data-el-lightbox-group="' + group + '"]'
      : '[data-el-lightbox-trigger]';
    var triggers = Array.prototype.slice.call(document.querySelectorAll(selector));

    _lightboxState.items = triggers.map(function (t) {
      return {
        src: t.getAttribute('data-el-lightbox-src') || (t.querySelector('img') && t.querySelector('img').src) || '',
        alt: t.getAttribute('data-el-lightbox-alt') || (t.querySelector('img') && t.querySelector('img').alt) || '',
        el: t,
      };
    }).filter(function (it) { return !!it.src; });

    _lightboxState.index = Math.max(0, triggers.indexOf(triggerEl));
    updateLightbox();
    try { dlg.showModal(); } catch (e) { debug(e); return; }
    /* Move focus to close button for clean keyboard flow */
    var closeBtn = dlg.querySelector('[data-el-lightbox-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeLightbox() {
    var dlg = _lightboxState.dialog || getLightboxDialog();
    if (dlg && dlg.open) dlg.close();
  }

  function stepLightbox(delta) {
    if (!_lightboxState.items.length) return;
    var len = _lightboxState.items.length;
    _lightboxState.index = (_lightboxState.index + delta + len) % len;
    updateLightbox();
  }

  function initLightbox() {
    var dlg = getLightboxDialog();
    /* Delegate triggers even without a dialog present yet — noop until markup lands */
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-el-lightbox-trigger]');
      if (trigger) {
        e.preventDefault();
        openLightbox(trigger);
        return;
      }
      var closer = e.target.closest('[data-el-lightbox-close]');
      if (closer) {
        e.preventDefault();
        closeLightbox();
        return;
      }
      var prev = e.target.closest('[data-el-lightbox-prev]');
      if (prev) {
        e.preventDefault();
        stepLightbox(-1);
        return;
      }
      var next = e.target.closest('[data-el-lightbox-next]');
      if (next) {
        e.preventDefault();
        stepLightbox(1);
      }
    });

    document.addEventListener('keydown', function (e) {
      var d = _lightboxState.dialog || getLightboxDialog();
      if (!d || !d.open) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); stepLightbox(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); stepLightbox(1); }
      /* Escape is native to <dialog>; browser closes it for free */
    });

    /* Click scrim (outside .el-lightbox__inner) closes */
    if (dlg) {
      dlg.addEventListener('click', function (e) {
        if (e.target === dlg) closeLightbox();
      });
    }
  }

  API.lightbox = { open: openLightbox, close: closeLightbox, next: function () { stepLightbox(1); }, prev: function () { stepLightbox(-1); } };

  /* =====================================================================
     5. Recent purchase ticker
     [data-el-recent-purchases] container with data-purchases='[...]' JSON.
     Rotates entries with fade every 5s. data-interval-ms overrides.
     Entry fields: name, city, product, minutes_ago.
  ===================================================================== */
  function parsePurchases(el) {
    var raw = el.getAttribute('data-purchases') || '[]';
    try { var arr = JSON.parse(raw); return Array.isArray(arr) ? arr : []; }
    catch (e) { debug(e); return []; }
  }

  function formatPurchase(p, lang) {
    var name = p.name || '—';
    var city = p.city || '';
    var product = p.product || '';
    var ago = parseInt(p.minutes_ago || 0, 10);
    if (lang === 'en') {
      var when = ago < 60 ? (ago + ' min ago') : (Math.floor(ago / 60) + ' h ago');
      return name + (city ? ' (' + city + ')' : '') + ' — ' + product + ' · ' + when;
    }
    var whenFr = ago < 60 ? ('il y a ' + ago + ' min') : ('il y a ' + Math.floor(ago / 60) + ' h');
    return name + (city ? ' (' + city + ')' : '') + ' — ' + product + ' · ' + whenFr;
  }

  function initTicker(el) {
    if (el.dataset.elTickerInit === '1') return;
    el.dataset.elTickerInit = '1';

    var items = parsePurchases(el);
    if (!items.length) { el.setAttribute('hidden', ''); return; }
    el.removeAttribute('hidden');

    var lang = (el.getAttribute('data-lang') || document.documentElement.getAttribute('lang') || 'fr').slice(0, 2);
    var interval = Math.max(1500, parseInt(el.getAttribute('data-interval-ms') || '5000', 10));

    var slot = el.querySelector('[data-el-recent-slot]') || el;
    var i = 0;

    function paint() {
      slot.classList.remove('is-in');
      /* next frame → force reflow → re-add for transition */
      requestAnimationFrame(function () {
        slot.textContent = formatPurchase(items[i], lang);
        requestAnimationFrame(function () { slot.classList.add('is-in'); });
      });
    }

    paint();
    var timer = setInterval(function () {
      i = (i + 1) % items.length;
      paint();
    }, interval);

    /* Pause while tab hidden */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clearInterval(timer);
    });
  }

  function initTickers() {
    document.querySelectorAll('[data-el-recent-purchases]').forEach(initTicker);
  }

  /* =====================================================================
     6. Wishlist (localStorage-backed)
     Buttons: [data-el-wishlist-toggle="<product-id>"] → toggles a heart.
     Counters: [data-el-wishlist-count] → total count.
     Pub/sub: ELOENCE_WISHLIST.subscribe(cb) — cb receives Array<id>.
     Event: document emits 'eloence:wishlist:changed' with detail.list.
  ===================================================================== */
  var WL_KEY = 'eloence_wishlist_v1';
  var _wlSubs = [];

  function wlRead() {
    try {
      var raw = localStorage.getItem(WL_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(String) : [];
    } catch (e) { debug(e); return []; }
  }

  function wlWrite(list) {
    try { localStorage.setItem(WL_KEY, JSON.stringify(list)); }
    catch (e) { debug(e); }
  }

  function wlList() { return wlRead(); }
  function wlHas(id) { return wlRead().indexOf(String(id)) !== -1; }

  function wlToggle(id) {
    var list = wlRead();
    var key = String(id);
    var idx = list.indexOf(key);
    if (idx === -1) list.push(key); else list.splice(idx, 1);
    wlWrite(list);
    wlPublish(list);
    return idx === -1;
  }

  function wlSubscribe(cb) {
    if (typeof cb !== 'function') return function () {};
    _wlSubs.push(cb);
    try { cb(wlRead()); } catch (e) { debug(e); }
    return function unsubscribe() {
      var i = _wlSubs.indexOf(cb);
      if (i !== -1) _wlSubs.splice(i, 1);
    };
  }

  function wlPublish(list) {
    _wlSubs.forEach(function (cb) { try { cb(list); } catch (e) { debug(e); } });
    document.dispatchEvent(new CustomEvent('eloence:wishlist:changed', { detail: { list: list } }));
    paintWishlistUI(list);
  }

  function paintWishlistUI(list) {
    if (!list) list = wlRead();
    var set = {};
    list.forEach(function (k) { set[k] = true; });

    document.querySelectorAll('[data-el-wishlist-toggle]').forEach(function (btn) {
      var id = btn.getAttribute('data-el-wishlist-toggle');
      var active = !!set[String(id)];
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    document.querySelectorAll('[data-el-wishlist-count]').forEach(function (el) {
      el.textContent = String(list.length);
    });
  }

  function initWishlist() {
    /* Delegated click — survives section re-renders */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-el-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-el-wishlist-toggle');
      if (!id) return;
      wlToggle(id);
    });

    /* Cross-tab sync via storage event */
    window.addEventListener('storage', function (e) {
      if (e.key === WL_KEY) {
        var list = wlRead();
        wlPublish(list);
      }
    });

    paintWishlistUI();
  }

  window.ELOENCE_WISHLIST = {
    list: wlList,
    has: wlHas,
    toggle: wlToggle,
    subscribe: wlSubscribe,
    /* Re-paint all buttons/counters after a section re-render replaced DOM nodes */
    refresh: function () { paintWishlistUI(); },
  };
  API.wishlist = window.ELOENCE_WISHLIST;

  /* =====================================================================
     Variant-change echo
     eloence-ui.js dispatches 'eloence:variant-change' (legacy name).
     Brief specifies 'eloence:variant:changed' as the shared event.
     We listen to the legacy one and re-dispatch under the canonical name
     on `document` so other agents can subscribe consistently.
  ===================================================================== */
  function initVariantEventBridge() {
    window.addEventListener('eloence:variant-change', function (e) {
      document.dispatchEvent(new CustomEvent('eloence:variant:changed', { detail: e.detail || {} }));
    });
  }

  /* =====================================================================
     Boot
  ===================================================================== */
  ready(function () {
    initVariantEventBridge();
    initCountdowns();
    initStockBars();
    initZooms();
    initLightbox();
    initTickers();
    initWishlist();
  });

  /* Theme-editor: re-init features when a section is re-rendered.
     Doc-level click/keydown handlers already survive via delegation — we only
     need to re-paint stateful DOM (stock bars, tickers, zoom, wishlist). */
  document.addEventListener('shopify:section:load', function () {
    initCountdowns();
    initStockBars();
    initZooms();
    initTickers();
    paintWishlistUI();
  });

  window.ELOENCE_CRO = API;
})();
